/**
 * FileMakerBridge.ts - Core bridge between FileMaker and JavaScript
 * 
 * CHANGE HISTORY:
 * - 251101_1415 [pending] danielbeer: Initial creation with environment detection and script calling
 */

import type { FileMakerBridge as IFileMakerBridge, FileMakerEnvironment, FileMakerCallbackResult } from './FileMakerTypes';

declare global {
  interface Window {
    FileMaker?: IFileMakerBridge;
  }
}

class FileMakerBridge {
  private environment: FileMakerEnvironment;
  private callbacks: Map<string, (result: any) => void> = new Map();
  private callbackId = 0;

  constructor() {
    this.environment = this.detectEnvironment();
    this.setupGlobalCallbacks();
  }

  /**
   * Detect FileMaker environment
   */
  private detectEnvironment(): FileMakerEnvironment {
    const hasFileMaker = typeof window.FileMaker !== 'undefined';
    const hasPerformScript = hasFileMaker && typeof window.FileMaker?.PerformScript === 'function';

    // Detect platform
    let platform: FileMakerEnvironment['platform'] = 'unknown';
    
    if (hasFileMaker) {
      const userAgent = navigator.userAgent.toLowerCase();
      
      if (userAgent.includes('fmwebdirect')) {
        platform = 'webdirect';
      } else if (userAgent.includes('mac') || navigator.platform.toLowerCase().includes('mac')) {
        platform = 'mac';
      } else if (userAgent.includes('win') || navigator.platform.toLowerCase().includes('win')) {
        platform = 'windows';
      }
    }

    // Try to detect version
    const version = this.detectVersion();

    return {
      platform,
      version,
      isFileMaker: hasFileMaker,
      hasPerformScript
    };
  }

  /**
   * Detect FileMaker version
   */
  private detectVersion(): string {
    // Try to extract version from user agent or other sources
    const userAgent = navigator.userAgent;
    const versionMatch = userAgent.match(/FileMaker\/(\d+\.\d+\.\d+)/);
    return versionMatch ? versionMatch[1] : 'unknown';
  }

  /**
   * Setup global callback handlers
   */
  private setupGlobalCallbacks(): void {
    // Expose callback handler to FileMaker
    (window as any).fmwvtool_handleCallback = (callbackId: string, result: string) => {
      const callback = this.callbacks.get(callbackId);
      if (callback) {
        try {
          const parsedResult = JSON.parse(result);
          callback(parsedResult);
        } catch (e) {
          callback({ success: false, error: 'Failed to parse callback result' });
        }
        this.callbacks.delete(callbackId);
      }
    };
  }

  /**
   * Get environment information
   */
  getEnvironment(): FileMakerEnvironment {
    return { ...this.environment };
  }

  /**
   * Check if running in FileMaker
   */
  isFileMaker(): boolean {
    return this.environment.isFileMaker;
  }

  /**
   * Call FileMaker script
   */
  callScript(scriptName: string, parameter?: any): void {
    if (!this.environment.hasPerformScript) {
      console.warn(`FileMaker.PerformScript unavailable; script="${scriptName}" param=${JSON.stringify(parameter)}`);
      return;
    }

    try {
      const paramString = typeof parameter === 'string' ? parameter : JSON.stringify(parameter ?? {});
      window.FileMaker!.PerformScript(scriptName, paramString);
    } catch (e) {
      console.error('FileMaker bridge error:', e);
    }
  }

  /**
   * Call FileMaker script with callback
   */
  callScriptWithCallback(scriptName: string, parameter?: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.environment.hasPerformScript) {
        reject(new Error('FileMaker.PerformScript unavailable'));
        return;
      }

      // Generate callback ID
      const callbackId = `cb_${this.callbackId++}_${Date.now()}`;
      
      // Store callback
      this.callbacks.set(callbackId, resolve);

      // Add callback ID to parameter
      const paramWithCallback = {
        ...(typeof parameter === 'object' ? parameter : { data: parameter }),
        _callbackId: callbackId
      };

      try {
        const paramString = JSON.stringify(paramWithCallback);
        window.FileMaker!.PerformScript(scriptName, paramString);

        // Timeout after 30 seconds
        setTimeout(() => {
          if (this.callbacks.has(callbackId)) {
            this.callbacks.delete(callbackId);
            reject(new Error('FileMaker script callback timeout'));
          }
        }, 30000);
      } catch (e) {
        this.callbacks.delete(callbackId);
        reject(e);
      }
    });
  }

  /**
   * Ping FileMaker to test connection
   */
  ping(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.environment.hasPerformScript) {
        resolve(false);
        return;
      }

      const result: FileMakerCallbackResult = {
        success: true,
        timestamp: Date.now()
      };

      this.callScript('ReceiveFromJS', result);
      resolve(true);
    });
  }

  /**
   * Log message to FileMaker
   */
  log(message: string, level: 'info' | 'warn' | 'error' = 'info'): void {
    this.callScript('LogFromJS', {
      message,
      level,
      timestamp: Date.now()
    });
  }

  /**
   * Register a function to be called from FileMaker
   */
  registerFunction(name: string, fn: (...args: any[]) => any): void {
    (window as any)[name] = fn;
  }

  /**
   * Unregister a function
   */
  unregisterFunction(name: string): void {
    delete (window as any)[name];
  }
}

// Singleton instance
let bridgeInstance: FileMakerBridge | null = null;

export function getFileMakerBridge(): FileMakerBridge {
  if (!bridgeInstance) {
    bridgeInstance = new FileMakerBridge();
  }
  return bridgeInstance;
}

export { FileMakerBridge };
